import type { MouseEvent, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface SectionLinkProps {
  /** Home section id, e.g. "services" or "#services" */
  section: string;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
}

/** Paths that render the home page. */
export const HOME_PATHS = ['/', '/services', '/approach', '/outcomes'];

/*
 * LOCKED — DO NOT MODIFY. Verified in preview + production.
 * Rules: section links must be plain paths (no "#" or "?") — the preview host mishandles
 * those. Never intercept clicks globally; never gate navigation on transition state.
 */
/**
 * Links to a home-page section via a plain path ("/services").
 * Plain paths work no matter who handles the click (router, preview host, full load).
 * Only extra: when already on the home page, scroll smoothly in place.
 */
export default function SectionLink({ section, className = '', children, onClick }: SectionLinkProps) {
  const id = section.replace(/^#/, '');
  const { pathname } = useLocation();

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.();
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    const el = document.getElementById(id);
    if (HOME_PATHS.includes(pathname) && el) {
      e.preventDefault();
      el.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
    // Otherwise: let the normal link navigate to "/{id}".
  };

  return (
    <Link to={`/${id}`} onClick={handleClick} className={className}>
      {children}
    </Link>
  );
}